import { useUserInfo } from "@/api/user";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { ROUTES } from "@/constants/routes";
import { useIsCanvasCentricApp } from "@/context/CanvasCentricContext";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import { useToggleTheme } from "@/hooks/useToggleTheme";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import type { OpenSettingsInput } from "@/utils/settingsNavigation";
import { useAuth0 } from "@auth0/auth0-react";
import { useThemeContext } from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ChevronRight,
  LayoutGrid,
  LogOut,
  Moon,
  RefreshCw,
  Settings,
  Sun,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

function OrganizationIcon({
  org,
  onClick,
}: {
  org: { name: string; logo_url?: string };
  onClick?: () => void;
}) {
  const body = org.logo_url ? (
    <img
      src={org.logo_url}
      alt={`${org.name} logo`}
      className="h-8 w-8 rounded-lg object-cover"
    />
  ) : (
    <div
      className="flex h-8 w-8 items-center justify-center rounded-lg
                 bg-foreground/[0.06] dark:bg-n-95/[0.06]"
    >
      <span className="text-[13px] font-semibold text-foreground">
        {org.name.charAt(0)}
      </span>
    </div>
  );

  if (!onClick) return <div className="shrink-0">{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Organization settings"
      className="group/orgicon relative shrink-0 rounded-lg cursor-pointer
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {body}
      <span
        className="absolute inset-0 flex items-center justify-center rounded-lg
                   bg-n-95/55 text-white opacity-0
                   group-hover/orgicon:opacity-100 focus-visible:opacity-100
                   transition-opacity duration-150"
      >
        <Settings className="h-4 w-4" />
      </span>
    </button>
  );
}

function ThemeToggleButton() {
  const { mode } = useThemeContext();
  const { toggleTheme } = useToggleTheme();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label="Toggle theme"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                 text-muted-foreground hover:text-foreground
                 hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05]
                 transition-colors cursor-pointer"
    >
      {mode === "dark" ? (
        <Sun className="h-[15px] w-[15px]" />
      ) : (
        <Moon className="h-[15px] w-[15px]" />
      )}
    </button>
  );
}

// Shared menu row styled like the WorkspaceSidebar NavItem
function MenuRow({
  icon: Icon,
  label,
  onClick,
  destructive = false,
  className,
}: {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
  destructive?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2.5 h-9 rounded-lg px-3 text-left",
        "transition-colors cursor-pointer",
        destructive
          ? "text-destructive hover:bg-destructive/10"
          : "text-foreground/75 hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05]",
        className,
      )}
    >
      <Icon
        className={cn(
          "h-[15px] w-[15px] shrink-0 mb-0.5",
          destructive ? "text-destructive" : "text-muted-foreground/70",
        )}
      />
      <span className="flex-1 min-w-0 truncate text-[13px] font-medium">
        {label}
      </span>
    </button>
  );
}

function getSubscriptionStatus(subscription: {
  status?: string;
  current_period_end?: string;
  cancel_at?: string;
}): { text: string; className: string } | null {
  if (!subscription?.status) return null;
  const status = subscription.status;
  const currentPeriodEnd = subscription.current_period_end;
  const cancelAt = subscription.cancel_at;
  const isScheduledForCancellation = !!cancelAt && status !== "canceled";

  switch (status) {
    case "active":
      if (isScheduledForCancellation && cancelAt) {
        return {
          text: `Ends ${formatDistanceToNow(new Date(cancelAt), {
            addSuffix: true,
          })}`,
          className:
            "bg-spectrum-orange-light/30 text-lc-warning dark:bg-spectrum-orange-dark/30 dark:text-lc-warning",
        };
      }
      return {
        text: "Active",
        className:
          "bg-spectrum-green-light/30 text-green-800 dark:bg-spectrum-green-dark/30 dark:text-lc-active",
      };
    case "trialing":
      if (isScheduledForCancellation && cancelAt) {
        return {
          text: `Trial ends ${formatDistanceToNow(new Date(cancelAt), {
            addSuffix: true,
          })}`,
          className:
            "bg-spectrum-orange-light/30 text-lc-warning dark:bg-spectrum-orange-dark/30 dark:text-lc-warning",
        };
      }
      const trialEndDate = currentPeriodEnd
        ? formatDistanceToNow(new Date(currentPeriodEnd), { addSuffix: true })
        : "";
      return {
        text: trialEndDate ? `Trial ends ${trialEndDate}` : "Free Trial",
        className:
          "bg-spectrum-blue-light/30 text-teal dark:bg-spectrum-blue-dark/30 dark:text-spectrum-blue-light",
      };
    case "canceled":
      return { text: "Cancelled", className: "" };
    case "past_due":
      return {
        text: "Past Due",
        className:
          "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      };
    case "incomplete":
      return {
        text: "Incomplete",
        className:
          "bg-yellow-100 text-gold dark:bg-yellow-900/30 dark:text-yellow-300",
      };
    case "unpaid":
      return {
        text: "Unpaid",
        className:
          "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
      };
    default:
      return {
        text: status.charAt(0).toUpperCase() + status.slice(1),
        className: "",
      };
  }
}

export interface AccountPopoverProps {
  children: React.ReactNode;
  /** When provided, opens settings via this callback instead of the context hook. */
  onOpenSettings?: (input?: OpenSettingsInput) => void;
  /** Popover placement. If undefined, uses responsive: right on md+, bottom on smaller. */
  popoverSide?: "right" | "bottom" | "top";
}

export function AccountPopover({
  children,
  onOpenSettings,
  popoverSide,
}: AccountPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const md = useBreakpoint("md");
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const isOrgAdmin = useIsOrgAdmin();
  const { logout, loginWithRedirect } = useAuth0();
  const flags = useFlags<Flags>();
  const { isCanvasCentricApp, setCanvasCentricApp } = useIsCanvasCentricApp();
  const contextOpenSettings = useOpenSettings();

  const openSettings = onOpenSettings ?? contextOpenSettings;

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const subscription = userInfo?.subscription;
  const currentPlan = subscription?.plan_name;
  const subscriptionStatus = getSubscriptionStatus(subscription ?? {});

  const side = popoverSide ?? (md ? "right" : "bottom");

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        data-testid="user-avatar-popover"
        className="w-[300px] p-2 rounded-2xl border dark:border-white/[0.08] shadow-xl shadow-black/10 bg-background/95 backdrop-blur-lg"
        collisionPadding={20}
        align="start"
        side={side}
      >
        {/* User header */}
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-semibold text-foreground truncate">
              {userInfo?.user.name}
            </p>
            <p className="text-[12px] text-muted-foreground truncate mt-0.5">
              {userInfo?.user.email}
            </p>
          </div>
          <ThemeToggleButton />
        </div>

        {/* Organization */}
        {userInfo?.organization && (
          <>
            <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
            <div className="flex items-center gap-3 px-3 py-2.5">
              <OrganizationIcon
                org={userInfo.organization}
                onClick={() => {
                  setIsOpen(false);
                  openSettings(ROUTES.SETTINGS.ORGANIZATION);
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-foreground truncate">
                  {userInfo.organization.name}
                </p>
              </div>
            </div>

            {/* Plan + Manage on its own row */}
            {currentPlan && flags.billing && (
              <>
                <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <span className="text-[12px] text-muted-foreground">
                      {currentPlan.charAt(0).toUpperCase()}
                      {currentPlan.slice(1)} plan
                    </span>
                    {subscriptionStatus && (
                      <Badge
                        variant="muted"
                        className={cn(
                          "text-[10px] px-1.5 py-0 rounded font-medium leading-4",
                          subscriptionStatus.className,
                        )}
                      >
                        {subscriptionStatus.text}
                      </Badge>
                    )}
                  </div>
                  {isOrgAdmin && (
                    <Button
                      data-testid="manage-billing-button"
                      size="xs"
                      variant="ghost"
                      className="shrink-0 h-7 rounded-lg text-[12px] text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        analytics.trackBillingManageClicked({
                          source: "sidebar",
                        });
                        setIsOpen(false);
                        openSettings(ROUTES.SETTINGS.USAGE_BILLING);
                      }}
                    >
                      Manage
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* Switch organization */}
        {userInfo?.organizations && userInfo.organizations.length > 1 && (
          <>
            <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
            <MenuRow
              icon={RefreshCw}
              label="Switch Organization"
              onClick={() => {
                loginWithRedirect({
                  authorizationParams: {
                    prompt: "select_account",
                    ignoreCache: true,
                  },
                });
              }}
            />
          </>
        )}

        {/* Canvas-centric toggle */}
        {flags.canvasCentricAppAdminToggle && (
          <>
            <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
            <div className="flex items-center justify-between gap-2 h-9 rounded-lg px-3">
              <Label
                htmlFor="canvas-centric-toggle"
                className="flex items-center gap-2.5 text-[13px] font-medium text-foreground/75 cursor-pointer flex-1"
              >
                <LayoutGrid className="h-[15px] w-[15px] shrink-0 text-muted-foreground/70" />
                <span>Canvas-centric app</span>
              </Label>
              <Switch
                id="canvas-centric-toggle"
                checked={isCanvasCentricApp}
                onCheckedChange={(checked) => {
                  setCanvasCentricApp(!!checked);
                  setIsOpen(false);
                  navigate(ROUTES.HOME);
                }}
              />
            </div>
          </>
        )}

        {/* Settings */}
        <>
          <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
          <MenuRow
            icon={Settings}
            label="Settings"
            onClick={() => {
              setIsOpen(false);
              openSettings();
            }}
          />
        </>

        {/* Logout */}
        <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />
        <MenuRow
          icon={LogOut}
          label="Log out"
          destructive
          onClick={handleLogout}
        />
      </PopoverContent>
    </Popover>
  );
}
